import * as React from 'react';

import PropTypes from 'prop-types';

import createLoadableComponent from 'js/lib/createLoadableComponent';
import user from 'js/lib/user';

import type { ThirdPartyOrganization } from 'bundles/admin-dashboard/types';
import type { DataPrivacyAgreementType } from 'bundles/enterprise-admin-onboarding/components/DPA/types';
import DataPrivacyAgreementsV1 from 'bundles/naptimejs/resources/dataPrivacyAgreements.v1';

const LoadableDPAPage = createLoadableComponent(
  () => import('bundles/enterprise-admin-onboarding/components/DPA/ResellerDPAPage')
);

type State = {
  dpas: {
    dpaType: DataPrivacyAgreementType | null;
    programId?: string;
  }[];
  currentDpa: number;
};

export type Props = {
  thirdPartyOrganization: ThirdPartyOrganization;
};

export const withResellerDPA = (Component: React.ComponentType<Props>) =>
  class extends React.Component<Props, State> {
    state: State = {
      dpas: [],
      currentDpa: 0,
    };

    static contextTypes = {
      executeMutation: PropTypes.func,
    };

    componentDidMount() {
      const { thirdPartyOrganization } = this.props;
      const { executeMutation } = this.context;

      if (!user.isAuthenticatedUser()) return Promise.resolve();

      const dpa = DataPrivacyAgreementsV1.action('isDPANeeded', {}, { orgId: thirdPartyOrganization.id });
      return executeMutation(dpa)
        .then(
          (response: {
            data: {
              dataPrivacyAgreements: {
                dataPrivacyAgreementType: DataPrivacyAgreementType;
                orgId: string;
                programId?: string;
              }[];
            };
          }) => {
            return this.setState({
              dpas:
                response.data.dataPrivacyAgreements?.map((agreement) => ({
                  dpaType: agreement?.dataPrivacyAgreementType,
                  programId: agreement?.programId,
                })) ?? [],
            });
          }
        )
        .catch(() => {
          // Do nothing, keep the DPAType as null
        });
    }

    onSubmit = () => {
      this.setState((state) => ({ currentDpa: state.currentDpa + 1 }));
    };

    render() {
      const { thirdPartyOrganization } = this.props;
      const { dpas, currentDpa } = this.state;

      if (dpas[currentDpa]) {
        return (
          <LoadableDPAPage
            onSubmit={this.onSubmit}
            orgId={thirdPartyOrganization.id}
            dpaType={dpas[currentDpa].dpaType}
            programId={dpas[currentDpa].programId}
          />
        );
      } else {
        return <Component {...this.props} />;
      }
    }
  };
